import React, { useState, useCallback, useMemo } from "react";
import {
  Series,
  HorizontalBarChartDataPoint,
  TreemapChart,
} from "../../../components/Charts";
import { ResponsiveSelect } from "../../../components/MobileForm";
import {
  ChartSkeleton,
  ChartSkeletonBody,
  ChartSkeletonTitle,
  TrendAndTreemapWrapper,
  BarChartCard,
  TreemapCard,
  PeriodControls,
  ChartTitle,
  ChartCardHeader,
  ChartTitleRow,
} from "../styles";
import { ManualInvestmentsHint } from "./ManualInvestmentsHint";
import { TrendChartContent } from "./TrendChartContent";
import { BREAKPOINTS } from "../../../utils/constants";
import { TrendPeriod } from "../../../services";
import { useResponsive } from "../../../hooks/useResponsive";

interface TrendAndTreemapSectionProps {
  trendLoading: boolean;
  incomeTrendSeries: Series[];
  incomeTrendLabels: string[];
  topExpensesLoading: boolean;
  topExpensesChartData: HorizontalBarChartDataPoint[];
  availableYears: number[];
  onFetchTrend?: (period: TrendPeriod) => void;
  hasManualInvestmentAccounts: boolean;
}

export const TrendAndTreemapSection: React.FC<TrendAndTreemapSectionProps> =
  React.memo(
    ({
      trendLoading,
      incomeTrendSeries,
      incomeTrendLabels,
      topExpensesLoading,
      topExpensesChartData,
      availableYears,
      onFetchTrend,
      hasManualInvestmentAccounts,
    }) => {
      const isMobile = useResponsive(BREAKPOINTS.mobile);
      const treemapHeight = isMobile ? 300 : 450;

      const [selectedPeriod, setSelectedPeriod] =
        useState<TrendPeriod>("all-time");

      const handlePeriodChange = useCallback(
        (value: TrendPeriod) => {
          if (value === selectedPeriod) return;
          setSelectedPeriod(value);
          onFetchTrend?.(value);
        },
        [onFetchTrend, selectedPeriod],
      );

      const periodOptions = useMemo(
        () => [
          { value: "all-time", label: "All Time" },
          ...availableYears.map((y) => ({
            value: String(y),
            label: String(y),
          })),
        ],
        [availableYears],
      );
      const selectedPeriodOption = useMemo(
        () =>
          periodOptions.find((o) => o.value === selectedPeriod) ??
          periodOptions[0],
        [periodOptions, selectedPeriod],
      );
      const manualInvestmentsHint = hasManualInvestmentAccounts ? (
        <ManualInvestmentsHint />
      ) : null;

      return (
        <TrendAndTreemapWrapper
          as="section"
          aria-label="Income vs Expenses and Top Expense Categories"
        >
          <BarChartCard>
            <ChartCardHeader>
              <ChartTitleRow>
                <ChartTitle>Income · Expenses</ChartTitle>
                {manualInvestmentsHint}
              </ChartTitleRow>
              <PeriodControls role="group" aria-label="Chart time period">
                <ResponsiveSelect
                  isCompact={isMobile}
                  options={periodOptions}
                  value={selectedPeriodOption.value}
                  onChange={(option) =>
                    handlePeriodChange(option.value as TrendPeriod)
                  }
                  sheetTitle="Select period"
                  placeholder="All Time"
                />
              </PeriodControls>
            </ChartCardHeader>

            {trendLoading ? (
              <ChartSkeletonBody height={isMobile ? 350 : 500} />
            ) : (
              <TrendChartContent
                incomeTrendSeries={incomeTrendSeries}
                incomeTrendLabels={incomeTrendLabels}
              />
            )}
          </BarChartCard>

          <TreemapCard aria-label="Top expense categories treemap">
            {topExpensesLoading ? (
              <ChartSkeleton>
                <ChartSkeletonTitle />
                <ChartSkeletonBody height={treemapHeight} />
              </ChartSkeleton>
            ) : (
              <TreemapChart
                data={topExpensesChartData}
                title="Top expense categories"
                height={treemapHeight}
                emptyStateMessage="No expense data available"
              />
            )}
          </TreemapCard>
        </TrendAndTreemapWrapper>
      );
    },
  );

TrendAndTreemapSection.displayName = "TrendAndTreemapSection";
